<template>
    <BaseCard>
        <template #content>
            <div class="card-header">
                <h2>{{ headerDisplay }}</h2>
            </div>
            <EmptyState
                v-if="hasError && !isLowResponseCount"
                class="empty-state-container"
                icon="#icon-svs-chart"
                message="Nothing to see here yet"
                sub-message="Stay tuned - more SVS® insights are coming soon."
            />
            <div v-else class="card-content">
                <StudentGauge
                    :totalFeedback="feedback.feedbackCount"
                    :totalStudents="studentCount"
                    :studentType="name"
                    :color="color"
                    :emptyColor="emptyColor"
                />
                <BaseDivider />

                <!-- Unified feedback themes (handles both low response and normal states) -->
                <FeedbackThemes
                    :studentFeedback="studentFeedback"
                    :isLowResponseCount="isLowResponseCount"
                    @openSamples="openAiGeneratedModal"
                    @openFeedback="handleOpenRawFeedbackModal"
                />

                <!-- Unified Modal for both AI-generated and raw feedback -->
                <FeedbackModal
                    :isVisible="showModal"
                    :mode="modalMode"
                    :categoryName="modalCategoryName"
                    :summary="modalSummary"
                    :messages="modalMessages"
                    :chatbotFlowId="chatbotFlowId"
                    :isLoading="loadingRawMessages"
                    @close="closeModal"
                />
            </div>
        </template>
    </BaseCard>
</template>

<script setup>
import { BaseDivider, BaseCard } from '@edsights/ui-core';
import EmptyState from '@/components-deprecated/global/EmptyState';
import StudentGauge from './StudentGauge.vue';
import FeedbackModal from './FeedbackModal.vue';
import FeedbackThemes from './FeedbackThemes.vue';
import { useFeedbackModal } from '../composables/useFeedbackModal';
import { computed } from 'vue';

// props
const props = defineProps({
    studentType: {
        type: String,
        required: true
    },
    feedback: {
        type: Object,
        required: true
    },
    studentCount: {
        type: Number,
        required: true
    },
    chatbotFlowId: {
        type: String,
        required: false
    }
});

// use composable
const {
    modalMessages,
    showModal,
    modalMode,
    modalCategoryName,
    modalSummary,
    loadingRawMessages,
    openRawFeedbackModal,
    openAiGeneratedModal,
    closeModal
} = useFeedbackModal();

// vars
const studentTypes = {
    PROMOTER: {
        displayName: '🌟 Promoter Themes',
        name: 'promoters',
        emptyColor: '#CCE6D2',
        color: '#339D4D'
    },
    PASSIVE: {
        displayName: '🤔 Passive Themes',
        name: 'passives',
        emptyColor: '#F8E9BF',
        color: '#E6A700'
    },
    DETRACTOR: {
        displayName: '⚠️ Detractor Themes',
        name: 'detractors',
        emptyColor: '#F5C9D2',
        color: '#D9274B'
    }
};

// computed properties
const hasError = computed(() => {
    return studentFeedback.value.error;
});

const headerDisplay = computed(() => {
    return studentTypes[props.studentType]?.displayName;
});

const color = computed(() => {
    return studentTypes[props.studentType]?.color || 'black';
});

const emptyColor = computed(() => {
    return studentTypes[props.studentType]?.emptyColor || 'black';
});

const name = computed(() => {
    return studentTypes[props.studentType]?.name;
});

const studentFeedback = computed(() => {
    return props.feedback.summary;
});

const isLowResponseCount = computed(() => {
    return props.feedback.feedbackCount < 10;
});

// methods
const handleOpenRawFeedbackModal = () => {
    openRawFeedbackModal(props.chatbotFlowId, props.studentType);
};
</script>

<style scoped lang="scss">
@import '~@/styles/variables';

.card-header {
    padding: 1rem;
    flex-shrink: 0;
}

.card-header h2 {
    font-size: 1.25rem;
    color: #333;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.card-content {
    padding: 0 1rem;
    flex: 1;
    overflow: auto;
}

.helper-icon {
    padding-left: 0.5rem;
}

.empty-state-container {
    padding: 2rem 0;
}
</style>
